// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  ICertificationPurposeOfAssessment,
  ICertificationPurposeOfAssessmentCompetencyUnit,
} from 'interfaces/webpage/certification/registration/purpose-of-assessment'
import {
  ICertificationWebpagePurposeOfAssessmentApiDetailResponse,
  ICertificationWebpagePurposeOfAssessmentApiListResponse,
  ICertificationWebpagePurposeOfAssessmentCompetencyUnitApiListResponse,
} from 'interfaces/webpage/certification/registration/purpose-of-assessment/purpose-of-assessment-api.interface'
import {
  ICertificationWebpagePurposeOfAssessmentCompetencyUnitGetListAttrs,
  ICertificationWebpagePurposeOfAssessmentGetDetailAttrs,
  ICertificationWebpagePurposeOfAssessmentGetListAttrs,
  ICertificationWebpagePurposeOfAssessmentUpdateAttrs,
} from 'interfaces/webpage/certification/registration/purpose-of-assessment/purpose-of-assessment-view.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Loading
import { LOADING } from 'constant/shared.constant'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Loading
const { BASE, ACTION, DETAIL } = LOADING

// Types
const { model, maybeNull, optional, array, number, string } = types

const CertificationPurposeOfAssessmentModel = model('CertificationPurposeOfAssessment').props({
  id: maybeNull(number),
  name: maybeNull(string),
})

const CertificationPurposeOfAssessmentCompetencyUnitModel = model('CertificationPurposeOfAssessmentCompetencyUnit').props({
  id: maybeNull(number),
  name: maybeNull(string),
})

const CertificationPurposeOfAssessmentDetailModel = model('CertificationPurposeOfAssessmentDetail').props({
  id: maybeNull(number),
  purposeOfAssessment: maybeNull(string),
})

const CertificationWebpagePurposeOfAssessmentModel = types
  .model('CertificationWebpagePurposeOfAssessment')
  .props({
    certificationWebpagePurposeOfAssessment_loading: withHelper_commonLoadingModel,
    certificationWebpagePurposeOfAssessment_list: optional(array(CertificationPurposeOfAssessmentModel), []),
    certificationWebpagePurposeOfAssessment_competencyUnitList: optional(
      array(CertificationPurposeOfAssessmentCompetencyUnitModel),
      [],
    ),
    certificationWebpagePurposeOfAssessment_detail: optional(CertificationPurposeOfAssessmentDetailModel, {}),
  })
  .actions(self => ({
    certificationWebpagePurposeOfAssessment_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpagePurposeOfAssessment_loading, payload)

      detach(self.certificationWebpagePurposeOfAssessment_loading)
      self.certificationWebpagePurposeOfAssessment_loading = cast(map)
    },
    certificationWebpagePurposeOfAssessment_SET_LIST: (payload: ICertificationPurposeOfAssessment[]): void => {
      self.certificationWebpagePurposeOfAssessment_list = cast(payload)
    },
    certificationWebpagePurposeOfAssessment_SET_DETAIL: (payload: number | null): void => {
      if (payload !== null) {
        // Check if user want to update
        const detail = self.certificationWebpagePurposeOfAssessment_list.find(list => list.id === payload)

        if (detail) {
          detach(self.certificationWebpagePurposeOfAssessment_detail)
          self.certificationWebpagePurposeOfAssessment_detail = cast(cloneDeep(detail))
        }
      } else {
        self.certificationWebpagePurposeOfAssessment_detail = cast({})
      }
    },
    certificationWebpagePurposeOfAssessment_SET_COMPETENCY_UNIT_LIST: (
      payload: ICertificationPurposeOfAssessmentCompetencyUnit[],
    ): void => {
      self.certificationWebpagePurposeOfAssessment_competencyUnitList = cast(payload)
    },
    certificationWebpagePurposeOfAssessment_CLEAR_LIST: (): void => {
      self.certificationWebpagePurposeOfAssessment_list = cast([])
    },
    certificationWebpagePurposeOfAssessment_CLEAR_COMPETENCY_UNIT_LIST: (): void => {
      self.certificationWebpagePurposeOfAssessment_competencyUnitList = cast([])
    },
    certificationWebpagePurposeOfAssessment_CLEAR_DETAIL: (): void => {
      self.certificationWebpagePurposeOfAssessment_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch purpose of assessment competency unit list
     *
     * @param {ICertificationWebpagePurposeOfAssessmentCompetencyUnitGetListAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpagePurposeOfAssessment_fetchCompetencyUnitList: flow(function* (
      payload: ICertificationWebpagePurposeOfAssessmentCompetencyUnitGetListAttrs,
    ) {
      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpagePurposeOfAssessmentCompetencyUnitApiListResponse =
        yield self.environment.certificationWebpagePurposeOfAssessmentApi.certificationWebpagePurposeOfAssessment_getCompetencyUnitList(
          {
            params: { ...payload.params },
            requestConfig: { ...payload.requestConfig },
          },
        )

      // Do when response success
      if (response.ok) {
        self.certificationWebpagePurposeOfAssessment_SET_COMPETENCY_UNIT_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch purpose of assessment list
     *
     * @param {ICertificationWebpagePurposeOfAssessmentGetListAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpagePurposeOfAssessment_fetchList: flow(function* (
      payload: ICertificationWebpagePurposeOfAssessmentGetListAttrs,
    ) {
      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpagePurposeOfAssessmentApiListResponse =
        yield self.environment.certificationWebpagePurposeOfAssessmentApi.certificationWebpagePurposeOfAssessment_getList({
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpagePurposeOfAssessment_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Get purpose of assessment detail
     *
     * @param {ICertificationWebpagePurposeOfAssessmentGetDetailAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpagePurposeOfAssessment_fetchDetail: flow(function* (
      payload: ICertificationWebpagePurposeOfAssessmentGetDetailAttrs,
    ) {
      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: DETAIL, value: true })

      const response: ICertificationWebpagePurposeOfAssessmentApiDetailResponse =
        yield self.environment.certificationWebpagePurposeOfAssessmentApi.certificationWebpagePurposeOfAssessment_getDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpagePurposeOfAssessment_SET_DETAIL(response.res.data.id!)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Update purpose of assessment
     *
     * @param {ICertificationWebpagePurposeOfAssessmentUpdateAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpagePurposeOfAssessment_update: flow(function* (
      payload: ICertificationWebpagePurposeOfAssessmentUpdateAttrs,
    ) {
      const { assessmentId } = payload.params

      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: ACTION, value: true })

      // Set detail for UI
      self.certificationWebpagePurposeOfAssessment_SET_DETAIL(payload.body.purposeOfAssessmentId)

      const response: ICertificationWebpagePurposeOfAssessmentApiDetailResponse =
        yield self.environment.certificationWebpagePurposeOfAssessmentApi.certificationWebpagePurposeOfAssessment_update({
          params: { assessmentId },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        // When failed, back to previous value
        self.certificationWebpagePurposeOfAssessment_SET_DETAIL(payload.body.previousValue)

        if (payload?.onError) payload.onError()
      }

      self.certificationWebpagePurposeOfAssessment_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createCertificationWebpagePurposeOfAssessmentDefaultModel = () =>
  optional(CertificationWebpagePurposeOfAssessmentModel, {})
